import { resolveAudioSources } from '../../model/resolve-audio';
import type { PieceModel } from '../../model/types';

const SAMPLE_RATE = 48000;
const CHANNELS = 2;

/**
 * Mixes the piece's audio offline, straight from the model. Faster than real
 * time and identical every run, unlike capturing the live graph.
 */
export const renderPieceAudio = async (model: PieceModel): Promise<AudioBuffer | null> => {
  const sources = resolveAudioSources(model);
  const frames = Math.ceil((model.duration / 1000) * SAMPLE_RATE);
  if (sources.length === 0 || frames <= 0) return null;

  const context = new OfflineAudioContext({ numberOfChannels: CHANNELS, length: frames, sampleRate: SAMPLE_RATE });

  const decoded = new Map<string, AudioBuffer>();
  const uniqueSrcs = sources.map(source => source.src).filter((src, index, all) => all.indexOf(src) === index);

  await Promise.all(
    uniqueSrcs.map(async src => {
      try {
        const data = await fetch(src, { mode: 'cors' }).then(response => response.arrayBuffer());
        decoded.set(src, await context.decodeAudioData(data));
      } catch (error) {
        // A video with no audio track lands here; the rest of the mix still stands.
        console.warn('Skipped audio source', src, error);
      }
    })
  );

  let wired = 0;
  for (const source of sources) {
    const buffer = decoded.get(source.src);
    if (!buffer) continue;

    const node = context.createBufferSource();
    node.buffer = buffer;

    const gain = context.createGain();
    gain.gain.value = source.gain;

    node.connect(gain).connect(context.destination);
    node.start(source.start / 1000, source.offset / 1000, source.duration / 1000);
    wired++;
  }

  if (wired === 0) return null;

  return context.startRendering();
};
